import { useEffect, useRef, useState } from 'react';
import { assets, type Asset } from '../../api';
import { activeRole } from '../../stores/auth';

interface UploadProgress {
  name: string;
  size: number;
  loaded: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  deduplicated?: boolean;
}

export function AssetsPage() {
  const [list, setList] = useState<Asset[]>([]);
  const [filter, setFilter] = useState<'all' | 'image' | 'video' | 'pdf'>('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const myRole = activeRole();
  const canUpload = myRole === 'owner' || myRole === 'admin' || myRole === 'editor';
  const canDelete = myRole === 'owner' || myRole === 'admin';

  async function refresh() {
    try {
      const kind = filter === 'all' ? undefined : filter;
      setList((await assets.list(kind)).assets);
    } catch { setErr('Failed to load assets.'); }
  }
  useEffect(() => { refresh(); }, [filter]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setErr(null); setBusy(true);
    const initial: UploadProgress[] = Array.from(files).map((f) => ({
      name: f.name, size: f.size, loaded: 0, status: 'uploading',
    }));
    setUploads(initial);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const result = await assets.upload(f, undefined, (loaded, total) => {
          setUploads((cur) => cur.map((u, idx) => idx === i ? { ...u, loaded, size: total || u.size } : u));
        });
        setUploads((cur) => cur.map((u, idx) => idx === i
          ? { ...u, status: 'done', loaded: u.size, deduplicated: result.deduplicated }
          : u));
      } catch (e: unknown) {
        const detail = ((e as { detail?: { error?: string } }).detail?.error);
        const msg = detail === 'unsupported_mime_type'
          ? 'Unsupported file type'
          : detail || 'Upload failed';
        setUploads((cur) => cur.map((u, idx) => idx === i ? { ...u, status: 'error', error: msg } : u));
      }
    }
    refresh();
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    // Clear "done" rows after a short delay; keep errors visible until next upload.
    setTimeout(() => {
      setUploads((cur) => cur.filter((u) => u.status === 'error'));
    }, 3000);
  }

  async function remove(a: Asset) {
    if (!confirm(`Delete ${a.name}? Any package referencing it will break.`)) return;
    await assets.remove(a.id);
    refresh();
  }

  const visible = list.filter((a) =>
    !search || a.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="toolbar">
        <h1>Assets</h1>
        <div style={{ color: 'var(--fg-3)', fontSize: '0.88em', marginLeft: '1em' }}>
          Org-wide media library. Reused across packages.
        </div>
        <div className="spacer" />
        {canUpload && (
          <>
            <input ref={fileRef} type="file" multiple hidden
              accept="image/*,video/*,application/pdf"
              onChange={(e) => handleFiles(e.target.files)} />
            <button className="primary" onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? 'Uploading…' : '+ Upload'}
            </button>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.6em', alignItems: 'center', marginBottom: '1em' }}>
        <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}
          style={{ width: 'auto' }}>
          <option value="all">All</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
          <option value="pdf">PDFs</option>
        </select>
        <input placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }} />
        <div style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}>{visible.length} of {list.length}</div>
      </div>

      {err && <div className="error">{err}</div>}

      {uploads.length > 0 && (
        <div className="card" style={{ marginBottom: '1em' }}>
          {uploads.map((u, i) => (
            <UploadRow key={i} u={u} />
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="empty">
          {filter === 'all' ? 'No assets yet. Upload images, videos or PDFs to get started.' : `No ${filter}s yet.`}
        </div>
      ) : (
        <div style={{
          display: 'grid', gap: '0.8em',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        }}>
          {visible.map((a) => (
            <AssetTile key={a.id} asset={a} canDelete={canDelete} onDelete={() => remove(a)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AssetTile({ asset, canDelete, onDelete }: { asset: Asset; canDelete: boolean; onDelete: () => void }) {
  const isImage = asset.kind === 'image';
  return (
    <div className="card" style={{ margin: 0, padding: '0.6em' }}>
      <div style={{
        height: 130, background: 'var(--bg-2)', borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {isImage ? (
          <img src={assets.rawUrl(asset.id)} alt={asset.name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ color: 'var(--fg-3)', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.9em' }}>
            <div style={{ fontSize: '2em', marginBottom: '0.3em' }}>
              {asset.kind === 'video' ? '▶' : '📄'}
            </div>
            {asset.kind.toUpperCase()}
          </div>
        )}
      </div>
      <div style={{ marginTop: '0.5em', fontWeight: 500, fontSize: '0.92em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={asset.name}>
        {asset.name}
      </div>
      <div style={{ color: 'var(--fg-3)', fontSize: '0.78em', marginTop: '0.2em' }}>
        {formatBytes(asset.size_bytes)} · {asset.kind}
      </div>
      {canDelete && (
        <button className="danger" style={{ marginTop: '0.4em', fontSize: '0.82em', padding: '0.3em 0.6em' }}
          onClick={onDelete}>Delete</button>
      )}
    </div>
  );
}

function UploadRow({ u }: { u: UploadProgress }) {
  const pct = u.size > 0 ? Math.min(100, Math.round((u.loaded / u.size) * 100)) : 0;
  const color = u.status === 'error' ? 'var(--danger, #ef4444)'
    : u.status === 'done' ? 'var(--success, #10b981)'
    : 'var(--accent)';
  return (
    <div style={{ padding: '0.5em 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.85em', marginBottom: '0.3em' }}>
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.name}>
          {u.name}
        </div>
        <div style={{ color: 'var(--fg-3)', marginLeft: '1em' }}>
          {u.status === 'uploading' && `${formatBytes(u.loaded)} / ${formatBytes(u.size)} (${pct}%)`}
          {u.status === 'done' && (u.deduplicated ? `Already in library (${formatBytes(u.size)})` : `Done · ${formatBytes(u.size)}`)}
          {u.status === 'error' && (u.error ?? 'Failed')}
        </div>
      </div>
      <div style={{ height: 4, background: 'var(--bg-2)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${u.status === 'done' ? 100 : pct}%`,
          background: color, transition: 'width 120ms ease-out',
        }} />
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
